import { ResolveFn } from "@angular/router";
import {
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from "rxjs";
import { inject } from "@angular/core";
import { DashboardService } from "./dashboard.service";
import { FiledClaimUiModel } from "./models/filed-case-ui.model";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { ProviderGetDTO } from "../providers/provider.model";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { StoreGetDTO } from "../store-management/stores/store";
import { UnClaimUiModel } from "./models/unclaim-cases-ui.model";
import { ProviderClaimTypesService } from "src/app/services/apps/providers/provider-claim-types.service";
import { ProviderCaseStatusService } from "src/app/services/apps/providers/provider-case-status.service";
import { TimeUtil } from "src/app/shared/services/time-util.service";
import { ViewContextHistoryStateService } from "src/app/shared/services/view-context-history-state.service";
import {
  describeApiRequestFailed,
  describeRouteEntered,
  extractSupportErrorMessage,
} from "src/app/shared/helpers/contact-support-history.helper";
import { BillingServiceOverviewStore } from "../billing/billing-status-widget/billing-service-overview.store";
import { BillingServiceOverview } from "../billing/billing-status-widget/billing-service-overview.model";
import { RouteStore } from "src/app/shared/stores/route-store";
import { getRouteListContextData } from "../store-management/shared/route-list-context.helper";
import {
  buildDashboardFilterFromContext,
  DashboardFilterFormValue,
} from "./dashboard-view-state.cache";
import { ProfileService } from "../profile/profile.service";
import { isUserOnboardingComplete } from "../profile/profile.model";

export interface DashboardContextData {
  filterForm?: DashboardFilterFormValue;
}

export const dashboardResolver: ResolveFn<
  | {
      filedClaim: FiledClaimUiModel | null;
      unfiledClaim: UnClaimUiModel | null;
      providers: ProviderGetDTO[];
      stores: StoreGetDTO[];
      claimTypes: any[];
      caseStatuses: any[];
      usageOverview: BillingServiceOverview | null;
      usageOverviewError: string | null;
    }
  | { error: { message: string } }
> = (route, state) => {
  const dashboardService = inject(DashboardService);
  const providerSvc = inject(ProviderService);
  const storeSvc = inject(StoreService);
  const loaderSvc = inject(LoaderService);
  const providerClaimTypeSvc = inject(ProviderClaimTypesService);
  const providercaseStatusSvc = inject(ProviderCaseStatusService);
  const timeUtil = inject(TimeUtil);
  const billingOverviewStore = inject(BillingServiceOverviewStore);
  const routeStore = inject(RouteStore);
  const profileService = inject(ProfileService);
  loaderSvc.show();
  const viewContextHistorySvc = inject(ViewContextHistoryStateService);
  const loadDashboardKpis = isUserOnboardingComplete(
    profileService.userProfile(),
  );

  const defaultRange = timeUtil.getLastMonthsDateRange(12);
  const contextData = getRouteListContextData<DashboardContextData>(
    routeStore,
    state.url,
  );

  return forkJoin({
    providers: providerSvc.getCachedOrLoad(),
    stores: storeSvc.getCachedOrLoad(),
    claimTypes: providerClaimTypeSvc.getCachedOrLoad(),
    caseStatuses: providercaseStatusSvc.getCachedOrLoadByAppliesTo("case"),
  }).pipe(
    switchMap(({ caseStatuses, providers, stores, claimTypes }) => {
      const apiFilter = buildDashboardFilterFromContext(
        contextData,
        defaultRange,
        stores,
        providers,
      );

      const kpiRequests$ = loadDashboardKpis
        ? {
            filedClaim: dashboardService.getFilledClaim(caseStatuses, apiFilter),
            unfiledClaim: dashboardService.getUnfilledClaim(apiFilter),
          }
        : {
            filedClaim: of(null),
            unfiledClaim: of(null),
          };

      return forkJoin({
        ...kpiRequests$,
        usageOverview: billingOverviewStore.load().pipe(
          map((overview) => ({
            overview,
            errorMessage: billingOverviewStore.errorMessage(),
          })),
        ),
        providers: of(providers),
        stores: of(stores),
        claimTypes: of(claimTypes),
        caseStatuses: of(caseStatuses),
      });
    }),
    map(
      ({
        filedClaim,
        unfiledClaim,
        usageOverview,
        providers,
        stores,
        claimTypes,
        caseStatuses,
      }) => ({
        filedClaim,
        unfiledClaim,
        providers,
        stores,
        claimTypes,
        caseStatuses,
        usageOverview: usageOverview.overview,
        usageOverviewError: usageOverview.errorMessage,
      }),
    ),
    tap((resolvedData) => {
      viewContextHistorySvc.createContext({
        titleView: "Dashboard",
        eventAction: "route_entered",
        eventDescription: describeRouteEntered("Dashboard"),
        thisState: {
          filedClaim: resolvedData.filedClaim,
          unfiledClaim: resolvedData.unfiledClaim,
          providers: resolvedData.providers,
          stores: resolvedData.stores,
          claimTypes: resolvedData.claimTypes,
          caseStatuses: resolvedData.caseStatuses,
          usageOverview: resolvedData.usageOverview,
          usageOverviewError: resolvedData.usageOverviewError,
        },
        route,
        setAutomaticallyScreenshot: false,
      });
    }),
    finalize(() => {
      loaderSvc.hide();
    }),
    catchError((error) => {
      console.error("Error fetching dashboard data:", error);
      viewContextHistorySvc.createContext({
        titleView: "Dashboard",
        eventAction: "route_entered",
        eventDescription: describeApiRequestFailed(
          "load dashboard data in resolver",
          extractSupportErrorMessage(error),
        ),
        thisState: {
          error: { message: error.message },
        },
        route,
        setAutomaticallyScreenshot: false,
      });
      return of({ error: error });
    }),
  );

  // return forkJoin({
  //   filedClaim: dashboardService.getFilledClaim({
  //     ...timeUtil.getLastMonthsDateRange(12),
  //   }),
  //   unfiledClaim: dashboardService.getUnfilledClaim({
  //     ...timeUtil.getLastMonthsDateRange(12),
  //   }),
  //   providers: providerSvc.get(),
  //   stores: storeSvc.get(),
  //   claimTypes: providerClaimTypeSvc.get(),
  //   caseStatuses: providercaseStatusSvc.get(),
  // }).pipe(
  //   finalize(() => {
  //     loaderSvc.hide();
  //   }),
  //   catchError((error) => {
  //     console.error("Error fetching dashboard data:", error);

  //     return of({ error: error });
  //   })
  // );
};
